/* eslint-disable class-methods-use-this */
/* eslint-disable no-plusplus */
import { logger } from '@tecma/logs-fe';
import { saveAs } from 'file-saver';
import Cookies from 'js-cookie';
import JSZip from 'jszip';

import { ClientDocumentParams, Document } from 'types/document';
import { Area, AssetType, SpaceType } from 'types/project';
import { Statuses } from 'types/quote';
import { getCookie } from 'utils/getCookie';

import { JWT_TOKEN_COOKIE_KEY } from '../../../constants';
import { ENVIRONMENT } from '../../../constants/environment';

const initRequest: RequestInit = {
  referrerPolicy: 'unsafe-url',
  headers: {
    'Content-Type': 'application/json',
  },
};

type RequestContext = {
  area?: Area;
  assetTypology?: AssetType;
};

type RequestEndpoints = {
  advanceStatus: string;
  deleteRequest: string;
};

// TODO(sell): Add sale endpoints once the API is available.
const REQUEST_ENDPOINTS_BY_AREA: Partial<Record<Area, RequestEndpoints>> = {
  rent: {
    advanceStatus: '/v1/request/rent/advance-status',
    deleteRequest: '/v1/request/rent/soft-delete',
  },
};

const resolveRequestEndpoints = ({ area }: RequestContext = {}): RequestEndpoints => {
  // TODO(sell): Swap fallback behavior when sale endpoints are defined (consider explicit error instead of fallback).
  return REQUEST_ENDPOINTS_BY_AREA[area ?? 'rent'] ?? REQUEST_ENDPOINTS_BY_AREA.rent!;
};

export const getStatuses = async (projectId: string) => {
  const response = await fetch(`${ENVIRONMENT.REACT_APP_PRODUCT_CONFIGURATION_API_REST_URI}/v1/automata-configuration/${projectId}`, {
    ...initRequest,
    method: 'GET',
  });
  return response;
};

export const getRequestByClientId = async (projectId: string, clientId: string, statuses: Statuses, spaceId?: string) => {
  const token = Cookies.get(JWT_TOKEN_COOKIE_KEY);
  const response = await fetch(`${ENVIRONMENT.REACT_APP_CLIENT_API_REST_URI}/v1/request/project/${projectId}`, {
    ...initRequest,
    method: 'POST',
    headers: {
      ...initRequest.headers,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      status: Object.entries(statuses)
        .filter(([key]) => key.toLowerCase() !== 'cancelled')
        .flatMap(([, value]) => value.values),
      spaceId,
      clientId,
    }),
  });
  return response;
};

export const getSpace = async (projectId: string, spaceId: string, spaceType: SpaceType) => {
  try {
    const response = await fetch(`${ENVIRONMENT.REACT_APP_ASSET_API_REST_URI}/v1/spaces/project/${projectId}/${spaceType}/${spaceId}`, {
      ...initRequest,
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error(`Status: ${response.status}`);
    }
    return response;
  } catch (err) {
    logger.error(err);
    throw Error();
  }
};

export const getSpaces = async (projectId: string, spaceIds: string[], spaceType: SpaceType) => {
  try {
    // FIXME: remove mocked projectID when will implement Quotes endpoint
    const response = await fetch(`${ENVIRONMENT.REACT_APP_ASSET_API_REST_URI}/v1/spaces/project/${projectId}/${spaceType}`, {
      ...initRequest,
      method: 'POST',
      body: JSON.stringify({ spaceIds }),
    });
    if (!response.ok) {
      throw new Error(`Status: ${response.status}`);
    }
    return response;
  } catch (err) {
    logger.error(err);
    throw Error();
  }
};

export type EnrichedExtraSpace = {
  code: string;
  name: string;
  type: string;
};

export const getExtraSpacesByIds = async (projectId: string, spaceIds: string[]): Promise<EnrichedExtraSpace[]> => {
  if (!spaceIds.length) {
    return [];
  }
  const response = await fetch(`${ENVIRONMENT.REACT_APP_ASSET_API_REST_URI}/v1/spaces/project/${projectId}/extra/`, {
    ...initRequest,
    method: 'POST',
    body: JSON.stringify({ spaceIds }),
  });
  if (!response.ok) {
    throw new Error(`Status: ${response.status}`);
  }
  const { data } = await response.json();
  return (data ?? []).map((extra: { code?: string; name?: string; type?: string }) => ({
    code: extra.code ?? '',
    name: extra.name ?? '',
    type: extra.type ?? '',
  }));
};

export const getClientDocuments = async ({ clientId, projectId, quoteId }: ClientDocumentParams) => {
  try {
    const token = getCookie('jwtRent');
    const response = await fetch(
      `${ENVIRONMENT.REACT_APP_AWS_API_URI}/v1/clientDocument/getByClient?client=${clientId}&project_id=${projectId}&quote_id=${quoteId}`,
      {
        headers: {
          ...initRequest.headers,
          'x-api-key': ENVIRONMENT.REACT_APP_AWS_TOKEN || '',
          Authorization: `Bearer ${token}`,
        },
        method: 'GET',
      },
    );
    if (!response.ok) {
      throw new Error(`Status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    logger.error(error);
    throw Error();
  }
};

export const downloadZip = async (files: Document[], folderName: string) => {
  const zip = new JSZip();
  files.forEach((document) => {
    const byteCharacters = atob(document.base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const file = new Blob([byteArray], { type: document.type });
    zip.file(document.name, file);
  });
  await zip.generateAsync({ type: 'blob' }).then(function (content: Blob) {
    saveAs(content, folderName);
  });
};

export const advanceApplicationStatus = async (requestId: string, projectId: string, context: RequestContext = {}) => {
  const token = getCookie('jwtRent');
  const endpoints = resolveRequestEndpoints(context);
  const response = await fetch(`${ENVIRONMENT.REACT_APP_CLIENT_API_REST_URI}${endpoints.advanceStatus}/${requestId}`, {
    headers: {
      ...initRequest.headers,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      project_id: projectId,
    }),
    method: 'PUT',
  });
  if (!response.ok) {
    throw new Error(`Status: ${response.status}`);
  }
  const data = await response.json();
  return data;
};

export const callExternalSendRequest = async (
  externalApi: { url: string; method: string; headers: Record<string, string> },
  body: unknown,
) => {
  const response = await fetch(externalApi.url, {
    method: externalApi.method,
    headers: externalApi.headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Status: ${response.status}`);
  }
  return response;
};

export const deleteRequest = async (requestId: string, projectId: string, context: RequestContext = {}) => {
  const token = getCookie('jwtRent');
  const endpoints = resolveRequestEndpoints(context);
  const response = await fetch(`${ENVIRONMENT.REACT_APP_CLIENT_API_REST_URI}${endpoints.deleteRequest}/${requestId}`, {
    headers: {
      ...initRequest.headers,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      project_id: projectId,
    }),
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Status: ${response.status}`);
  }
  const data = await response.json();
  return data;
};
